"use client";

import { cn, assetPath } from "../../lib/utils";
import { AppIcon } from "./AppIcon";
import { tippStil, TIPP_KEYFRAMES } from "./TippSchrift";
import { AUFBAU_KLASSE } from "../ui/Aufbau";
import { HUELLE_MARKE, HUELLE_SCHRITT, huellenStil } from "./huellen-auftritt";

export interface AppWordmarkProps {
  /** Angezeigter App-Name, z.B. "Contacts". */
  name: string;
  /** Glyph-Squircle (AppIcon) vor dem Schriftzug. Default false. */
  showGlyph?: boolean;
  /** App-id für den AppIcon-Glyph-Lookup (APP_GLYPHS). */
  appId?: string;
  /** lucide-Glyph-Name als Fallback (neue Apps ohne Code-Eintrag). */
  glyphName?: string;
  /**
   * Dark-Mode aktiv — nur für die AppIcon-Hairline.
   *
   * Weglassen ist der Normalfall: `AppIcon` waehlt dann den CSS-Zweig
   * (`ring-black/10 dark:ring-white/12`), der ohne JavaScript korrekt ist und
   * beim ersten Paint schon stimmt. Nur setzen, wer den Modus BEWUSST abweichend
   * vom Dokument fuehrt — etwa eine Vorschau, die ein anderes Theme zeigt als
   * die Seite ringsum.
   */
  isDark?: boolean;
  /** Sheen-Glint beim Hover. Default true. */
  animated?: boolean;
  /**
   * Auftritt beim Oeffnen der App: das Zeichen steigt bei 0 ms auf, der
   * Schriftzug 40 ms spaeter und legt sich dabei Zeichen fuer Zeichen frei.
   *
   * Vorgabe `false`, weil diese Wortmarke auch dort steht, wo es keinen
   * Seiten-Aufbau gibt — im App-Wechsel-Vollbild, in der Mobil-Navigation, in
   * Vorschauen. Ein Auftritt mitten in einem schon stehenden Bild waere dort
   * kein Auftritt, sondern ein Zucken.
   */
  auftritt?: boolean;
  className?: string;
}

/**
 * App-Header-Wortmarke: der App-Name als echter Nevan-Schriftzug mit dem
 * monochromen Sheen-Glint der Website (statt statischem Logo-PNG). Optional das
 * App-Glyph davor. Font "Nevan" kommt aus dem globalen @font-face (jede App
 * serviert /fonts/Nevan.otf); Sheen + Gate leben in styles/globals.css. Reiner
 * Präsentations-Span — der Aufrufer (AppSwitcher) liefert den umschließenden Link.
 *
 * **Das Vorladen sitzt hier, nicht im Layout jeder App.** Ohne `<link rel="preload">`
 * entdeckt der Browser die Schrift erst, wenn er das CSS geparst UND ein Element
 * gefunden hat, das sie benutzt — die Wortmarke stand dadurch beim Neuladen kurz in
 * einer fremden Schrift (am 2026-08-19 gemeldet). Der Link gehört zu dem Baustein,
 * der die Schrift braucht; so kann keine App ihn vergessen. Next hebt ihn in den
 * `<head>`, auch aus einer Client-Komponente heraus.
 *
 * `crossOrigin` ist PFLICHT, obwohl die Datei von derselben Herkunft kommt:
 * Schrift-Abrufe aus CSS laufen immer im CORS-Modus, und ohne dieselbe Kennzeichnung
 * laedt der Browser die Datei ZWEIMAL — einmal vorab ungenutzt, einmal für das CSS.
 */
export function AppWordmark({
  name,
  showGlyph = false,
  appId,
  glyphName,
  // KEIN Default: `false` haette die Hell-Hairline auch im Dunkelmodus erzwungen,
  // sobald ein Aufrufer das Prop weglaesst. `undefined` reicht die Entscheidung an
  // den CSS-Zweig in AppIcon durch, der beide Modi von sich aus richtig trifft.
  isDark,
  animated = true,
  auftritt = false,
  className,
}: AppWordmarkProps) {
  const vorladen = (
    <link
      rel="preload"
      href={assetPath("/fonts/Nevan.otf")}
      as="font"
      type="font/otf"
      crossOrigin="anonymous"
    />
  );

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      {vorladen}
      {auftritt && <style>{TIPP_KEYFRAMES}</style>}
      {showGlyph && (
        // Squircle-Höhe = Schriftzug-Höhe (1.55rem): AppIcon füllt einen auf die
        // Font-Größe gesetzten Wrapper, statt fixer px — Icon und Wortmarke stehen
        // exakt gleich hoch.
        <span
          className={cn("inline-flex h-[1.55rem] w-[1.55rem] shrink-0", auftritt && AUFBAU_KLASSE)}
          style={auftritt ? huellenStil(HUELLE_MARKE) : undefined}
        >
          <AppIcon appId={appId} glyphName={glyphName} isDark={isDark} fill />
        </span>
      )}
      <span
        style={{
          fontFamily: '"Nevan", var(--font-geist-sans), sans-serif',
          // Optische Korrektur (nur mit Glyph): geometrisch ist der Schriftzug exakt
          // auf Icon-Mitte (pixelgemessen, offset 0). Neben dem massiven Squircle
          // wirken die Nevan-Versalien aber minimal zu hoch (die Glyph-Masse sitzt
          // selbst leicht unter der Icon-Mitte) → 0.04em (~1px) nach unten liest sich
          // bündig. Ohne Glyph steht der Schriftzug allein → keine Verschiebung.
          transform: showGlyph ? "translateY(0.04em)" : undefined,
        }}
        className={cn(
          "truncate text-[1.55rem] font-bold leading-none tracking-[-0.01em]",
          // Ohne Animation dieselbe Logik wie im Sheen: der Schriftzug traegt die
          // Themefarbe, nicht das neutrale foreground.
          animated ? "wordmark-sheen" : "text-primary",
          // Bezugsrahmen fuer den Deckel. `truncate` bringt `overflow: hidden`
          // schon mit, sonst schoebe der Deckel sich ueber den Nachbarn.
          auftritt && "relative",
          // KEIN `AUFBAU_KLASSE` hier, und das ist der Punkt: das Freilegen IST
          // der Auftritt des Schriftzugs. Beides zusammen hiess, dass sich der
          // Text freilegt, WAEHREND er noch einblendet — nachgemessen am
          // 2026-08-22 war er bei 14 % Freilegung erst zu 41 % deckend und bei
          // 100 % Freilegung zu 99 %. Die Animation lief also vollstaendig im
          // Unsichtbaren ab, und genau so wurde sie gemeldet: „sehe ich nicht".
        )}
      >
        {name}
        {/* Der Deckel, der den Namen Zeichen fuer Zeichen freigibt. Er liegt
            NEBEN dem Text und nicht auf ihm: der Sheen malt ueber
            `background-clip: text`, ein Beschnitt am Text selbst traefe dort
            nur transparente Glyphen. Und er haelt die `animation-*` vom
            Schriftzug fern, die sonst `.wordmark-sheen:hover` ueberschreibt. */}
        {auftritt && (
          <span aria-hidden data-jw-tippdeckel="" style={tippStil(name, HUELLE_MARKE + HUELLE_SCHRITT)} />
        )}
      </span>
    </span>
  );
}
